import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExtractionReviewValues } from "components/Document/ExtractionReview";
import { useCallback } from "react";
import { SHIPPING_PAPERWORK_QUERY } from "routes/Customer/Waste/Manifests/api";

import { APPLY_EXTRACTED_BILL_OF_LADING, EXTRACTED_BILL_OF_LADING } from "./api";
import { SavedShippingDocument, toApplyInput } from "./extractionReview";

/**
 * Applying a reviewed bill of lading reading.
 *
 * There is no `wasteShippingDocumentInputValidator` (unlike 7C's profile
 * apply), so nothing runs client-side before the mutation — the save either
 * succeeds or surfaces the server's rejection.
 *
 * Refetches the reading (its `candidateStatus` flips to APPLIED) and the
 * merged shipping-paperwork list, which is the only surface an applied bill of
 * lading lands on — the standalone shipping-documents list it also used to
 * refresh is retired, and the merged grid replaced it.
 *
 * `GetShippingDocument` is not refetched here. A fresh apply navigates to a
 * route that reads the document for the first time, and a re-review from the
 * detail page is covered by that page's own active query.
 */
export function useApplyReviewedBillOfLading({
  documentId,
  facilityId,
  existingId,
  saved,
  onApplied,
}: {
  documentId: string;
  facilityId: string;
  existingId?: string;
  /**
   * The document this apply will write over, when an earlier review already
   * saved one. Its lines are where a line the reviewer kept from the record
   * gets its waste stream and container back — the review grid has no columns
   * for either, so nothing else on this path knows them.
   */
  saved?: SavedShippingDocument | null;
  onApplied: (savedId: string) => void;
}) {
  const alerts = useAlerts();
  const [applyMutation, { loading: applying }] = useMutation(APPLY_EXTRACTED_BILL_OF_LADING, {
    refetchQueries: [EXTRACTED_BILL_OF_LADING, SHIPPING_PAPERWORK_QUERY],
  });

  const confirm = useCallback(
    async (reviewed: ExtractionReviewValues) => {
      try {
        const { data } = await applyMutation({
          variables: {
            input: toApplyInput(reviewed, { documentId, facilityId, existingId, saved }),
          },
        });
        const savedId = data?.applyExtractedBillOfLading?.id;
        if (!savedId) {
          throw new Error("The apply did not return a saved document.");
        }
        alerts.success("Bill of lading applied.");
        onApplied(savedId);
      } catch (error) {
        alerts.error(
          "Could not apply the bill of lading",
          error instanceof Error ? error.message : undefined
        );
      }
    },
    [alerts, applyMutation, documentId, facilityId, existingId, saved, onApplied]
  );

  return { confirm, applying };
}
